// api/targets.ts
/**
 * Client for the targets, budgets and guardrails API (Data Management → Targets).
 *
 * Backend: apps/backend/api/routes/targets_routes.py
 * BFF:     apps/bff/src/routes/targets.routes.ts
 * Plan:    docs/plans-pending/2026-09-02-targets-and-budgets.md (A9)
 *
 * Three shapes travel here and they are not interchangeable:
 *
 * * a **version** — a named plan ("Budget 2026", "3+9 forecast"). Exactly one
 *   is active per account and that is the one every dashboard, report, chat
 *   answer and monitor compares against. Every other version is editable and
 *   comparable but changes nothing until it is made active.
 * * a **target metric** — what a target is FOR ("Revenue"): one of Verity's
 *   (`catalog:<key>` until the account first uses it) or the account's own
 *   (`tm_…`). Its link to a source, dataset and metric is set in Settings and
 *   may be absent; a target of an unlinked target metric is stored and
 *   spread, and reaches dashboards once it is linked (plan
 *   2026-10-10-target-metrics-catalog-and-linking).
 * * a **line** — one stored target: a target metric, a kind, an optional
 *   slice, a period and a value. Read shapes carry a `tgt_…` public id.
 * * an **import row** — what the customer typed, pasted, uploaded or picked,
 *   as STRINGS. It has no id and is not stored: it goes through `validate`
 *   (which writes nothing) and then `apply`. That is why the row type here is
 *   all-strings while `TargetLine` is typed — they are different objects at
 *   different stages, not two views of one.
 *
 * Reads are open to every member; every write requires an account OWNER or
 * ADMIN and the backend enforces it (an MCP token is refused outright).
 */

import { apiFetch, apiJson, apiNoContent } from "./client";

const BASE = "/targets";

export type TargetKind = "target" | "budget" | "guardrail";
export type TargetComparator = "lte" | "gte";
export type AllocationMethod =
  | "linear"
  | "last_year_shape"
  | "weekday_profile"
  | "custom_weights";
export type AllocationKind = "spread" | "level";
export type ImportSourceKind = "file" | "sheet" | "paste" | "manual" | "mcp";

// ── Versions ───────────────────────────────────────────────────────────────

export type TargetVersion = {
  public_id: string;
  name: string;
  is_active: boolean;
  is_archived: boolean;
  notes: string | null;
  line_count: number;
  created_at: string;
  updated_at: string;
  activated_at: string | null;
};

export type TargetVersionListResponse = { versions: TargetVersion[] };

export type TargetVersionCompareRow = {
  key: {
    target_metric?: string;
    target_metric_label?: string;
    dataset_key?: string | null;
    metric_key?: string;
    kind?: string;
    dimension_key?: string;
    dimension_value?: string;
    period_label?: string;
    period_start?: string;
    aggregate_connector_type?: string | null;
    [extra: string]: unknown;
  };
  value_a: number | null;
  value_b: number | null;
  delta: number | null;
};

export type TargetVersionCompareResponse = {
  a: string;
  b: string;
  rows: TargetVersionCompareRow[];
};

// ── Lines ──────────────────────────────────────────────────────────────────

export type TargetLine = {
  public_id: string;
  version_id: string;
  /** The line's target metric (`tm_…`) and its name. */
  target_metric: string;
  target_metric_label: string;
  /** The target metric's link; all null while it is not linked. */
  source_id: string | null;
  source_name: string | null;
  aggregate_connector_type: string | null;
  dataset_key: string | null;
  metric_key: string | null;
  kind: TargetKind;
  comparator: TargetComparator | null;
  dimension_key: string;
  dimension_value: string;
  period_grain: string;
  period_start: string;
  period_end: string;
  period_label: string;
  value: number;
  /** The stored value exactly (`163500`, `0.325`); the grid seeds cells with it. */
  value_text: string;
  allocation_kind: AllocationKind;
  allocation_method: AllocationMethod | null;
  allocation_note: string | null;
  source: ImportSourceKind;
  updated_at: string;
};

export type TargetLineListResponse = {
  version: TargetVersion;
  lines: TargetLine[];
  total: number;
  limit: number;
  offset: number;
};

export type TargetLineFilters = {
  version_id?: string;
  dataset_key?: string;
  metric_key?: string;
  /** A target metric's `tm_…` id. */
  target_metric?: string;
  kind?: TargetKind;
  /** ISO date; keeps lines whose period ends on or after it. */
  period_from?: string;
  /** ISO date; keeps lines whose period starts on or before it. */
  period_to?: string;
  limit?: number;
  offset?: number;
};

// ── Catalog and summary ────────────────────────────────────────────────────

export type TargetsCatalogDimension = { key: string; label: string };

export type TargetsCatalogMetric = {
  dataset_key: string;
  dataset_label: string;
  metric_key: string;
  metric_label: string;
  allocation_kind: AllocationKind;
  format: string;
  direction: string | null;
  dimensions: TargetsCatalogDimension[];
};

export type TargetsCatalogSource = {
  source_id: string | null;
  aggregate_connector_type: string | null;
  name: string;
  metrics: TargetsCatalogMetric[];
};

export type TargetsCatalog = {
  sources: TargetsCatalogSource[];
  methods: AllocationMethod[];
  /** One line naming every accepted period token, straight from the backend. */
  period_shapes: string;
};

export type TargetsSummary = {
  active_version: { public_id: string; name: string } | null;
  line_count: number;
  by_kind: Record<string, number>;
  versions: number;
};

// ── Target metrics ─────────────────────────────────────────────────────────

/** What a target metric is compared against. */
export type TargetMetricLink = {
  /** What a link change takes as `source`: a data source id or `aggregate:<TYPE>`. */
  source: string;
  source_name: string;
  dataset_key: string;
  dataset_label: string | null;
  metric_key: string;
  metric_label: string | null;
  /** False when the linked source or metric is gone or not visible to you. */
  available: boolean;
};

export type TargetMetric = {
  /** `tm_…` for one the account has, `catalog:<key>` for an unused Verity one. */
  ref: string;
  public_id: string | null;
  catalog_key: string | null;
  label: string;
  description: string | null;
  format: string | null;
  allocation_kind: AllocationKind;
  default_kind: TargetKind;
  direction: "higher" | "lower" | null;
  is_custom: boolean;
  link: TargetMetricLink | null;
  suggested_link: TargetMetricLink | null;
  line_count: number;
  sliced_dimensions: string[];
};

export type TargetMetricListResponse = { metrics: TargetMetric[] };

export type TargetMetricLinkInput = { source: string; dataset_key: string; metric_key: string };

// ── Import ─────────────────────────────────────────────────────────────────

/**
 * One row on its way in. Every field is a string on purpose: the customer
 * typed it, and the backend owns turning it into a value or an error that
 * names the column. Never build this from a `TargetLine`.
 */
export type ImportRow = {
  row_number: number;
  source: string;
  metric: string;
  /**
   * The target metric, as the grid names it (`tm_…` / `catalog:<key>`).
   * Empty: a row with a `source` is a legacy row; without one, `metric`
   * names the target metric by its label.
   */
  target_metric?: string;
  kind: string;
  dimension: string;
  dimension_value: string;
  period: string;
  value: string;
  comparator: string;
  method: string;
  weights: string;
  percent_cell?: boolean;
  /**
   * A NUMBER cell in an uploaded workbook: the backend reads it with `.` as
   * the decimal point. Pass parsed rows through unchanged; a UI that lets the
   * customer edit a value must drop this flag for that row.
   */
  numeric_cell?: boolean;
  /** Where the value sat in a wide sheet (1-based line/column); parse only. */
  cell_row?: number | null;
  cell_col?: number | null;
};

export type ImportLayout = "auto" | "long" | "wide";

export type MetricChoice = {
  source_id: string;
  source_name: string;
  dataset_key: string;
  metric_key: string;
  metric_label: string;
};

/** What one (source, metric) label in a parsed sheet resolved to. */
export type LabelResolution = {
  source: string;
  metric: string;
  status: "resolved" | "unknown" | "ambiguous";
  choice: MetricChoice | null;
  candidates: MetricChoice[];
  /** The parsed rows carrying this label: the join key once a source was filled. */
  row_numbers: number[];
  /** A label without a source: the target metric it names, if any. */
  target_metric?: { ref: string; label: string } | null;
};

export type ImportParseResponse = {
  rows: ImportRow[];
  columns: string[];
  warnings: string[];
  layout?: "long" | "wide";
  labels?: LabelResolution[];
};

export type RowIssue = {
  row_number: number;
  column: string;
  message: string;
};

/** A row the backend accepted, normalised. Keys mirror `ValidatedRow.as_dict`. */
export type ValidatedImportRow = {
  row_number: number;
  target_metric: string;
  target_metric_label: string;
  source: string | null;
  source_id: string | null;
  aggregate_connector_type: string | null;
  dataset_key: string | null;
  metric_key: string | null;
  metric_label: string;
  kind: TargetKind;
  comparator: TargetComparator | null;
  dimension_key: string;
  dimension_value: string;
  period: string;
  period_grain: string;
  period_start: string;
  period_end: string;
  value: string;
  allocation_kind: AllocationKind;
  method: AllocationMethod | null;
  weights: string[] | null;
  notes: string[];
};

export type ImportValidateResponse = {
  version_id: string;
  version_name: string;
  rows: ValidatedImportRow[];
  errors: RowIssue[];
  warnings: RowIssue[];
  new_count: number;
  updated_count: number;
  removed_count?: number;
  ok: boolean;
};

export type ImportApplyResponse = {
  version_id: string;
  created: number;
  updated: number;
  line_ids: string[];
  notes: RowIssue[];
  removed?: number;
};

/**
 * Serialised-row budget, mirrored from the plan (D6).
 *
 * A row cap alone does not keep a request under the BFF's 1 MB body limit —
 * a worst-case row is about 700 bytes, so 2000 of them is 1.4 MB. Measured
 * here with `TextEncoder` before sending so the customer gets a sentence that
 * says what to do instead of a 413 from a proxy.
 */
export const MAX_IMPORT_ROWS = 2000;
export const MAX_IMPORT_BYTES = 800_000;

/** Bytes the rows will occupy on the wire, the `TileExportMenu` pattern. */
export function importRowsByteSize(rows: ImportRow[]): number {
  return new TextEncoder().encode(JSON.stringify(rows)).length;
}

// ── Calls: versions ────────────────────────────────────────────────────────

export async function listTargetVersions(
  includeArchived = false,
): Promise<TargetVersionListResponse> {
  const suffix = includeArchived ? "?include_archived=true" : "";
  return apiJson<TargetVersionListResponse>(`${BASE}/versions${suffix}`, {
    unwrapData: false,
  });
}

export async function createTargetVersion(body: {
  name: string;
  notes?: string | null;
  /** Public id of the version to start from; omit for an empty version. */
  copy_of?: string | null;
}): Promise<TargetVersion> {
  return apiJson<TargetVersion>(`${BASE}/versions`, {
    method: "POST",
    body: JSON.stringify(body),
    unwrapData: false,
  });
}

export async function updateTargetVersion(
  versionPublicId: string,
  body: { name?: string; notes?: string | null },
): Promise<TargetVersion> {
  return apiJson<TargetVersion>(
    `${BASE}/versions/${encodeURIComponent(versionPublicId)}`,
    { method: "PUT", body: JSON.stringify(body), unwrapData: false },
  );
}

export async function setActiveTargetVersion(
  versionPublicId: string,
): Promise<TargetVersion> {
  return apiJson<TargetVersion>(
    `${BASE}/versions/${encodeURIComponent(versionPublicId)}/set-active`,
    { method: "POST", unwrapData: false },
  );
}

export async function archiveTargetVersion(
  versionPublicId: string,
): Promise<TargetVersion> {
  return apiJson<TargetVersion>(
    `${BASE}/versions/${encodeURIComponent(versionPublicId)}/archive`,
    { method: "POST", unwrapData: false },
  );
}

export async function unarchiveTargetVersion(
  versionPublicId: string,
): Promise<TargetVersion> {
  return apiJson<TargetVersion>(
    `${BASE}/versions/${encodeURIComponent(versionPublicId)}/unarchive`,
    { method: "POST", unwrapData: false },
  );
}

export async function deleteTargetVersion(versionPublicId: string): Promise<void> {
  return apiNoContent(`${BASE}/versions/${encodeURIComponent(versionPublicId)}`, {
    method: "DELETE",
  });
}

export async function compareTargetVersions(
  a: string,
  b: string,
): Promise<TargetVersionCompareResponse> {
  const query = `?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`;
  return apiJson<TargetVersionCompareResponse>(`${BASE}/versions/compare${query}`, {
    unwrapData: false,
  });
}

// ── Calls: catalog and summary ─────────────────────────────────────────────

export async function getTargetsCatalog(): Promise<TargetsCatalog> {
  return apiJson<TargetsCatalog>(`${BASE}/catalog`, { unwrapData: false });
}

export async function getTargetsSummary(): Promise<TargetsSummary> {
  return apiJson<TargetsSummary>(`${BASE}/summary`, { unwrapData: false });
}

// ── Calls: target metrics ─────────────────────────────────────────────────

export async function listTargetMetrics(): Promise<TargetMetricListResponse> {
  return apiJson<TargetMetricListResponse>(`${BASE}/metrics`, { unwrapData: false });
}

/** What an account's own target metric can be shown as (percent needs `level`). */
export type CustomTargetMetricFormat = "currency" | "number" | "percent";

export async function createTargetMetric(body: {
  label: string;
  allocation_kind: AllocationKind;
  /** Left out for `number`, the default, so a backend without formats still accepts it. */
  format?: CustomTargetMetricFormat;
}): Promise<TargetMetric> {
  return apiJson<TargetMetric>(`${BASE}/metrics`, {
    method: "POST",
    body: JSON.stringify(body),
    unwrapData: false,
  });
}

/**
 * Rename and/or link. `link: null` unlinks; leave `link` out to keep it.
 * An unused Verity target metric (`catalog:<key>`) gets its row on the way.
 */
export async function updateTargetMetric(
  ref: string,
  body: { label?: string; link?: TargetMetricLinkInput | null; format?: CustomTargetMetricFormat },
): Promise<TargetMetric> {
  return apiJson<TargetMetric>(`${BASE}/metrics/${encodeURIComponent(ref)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    unwrapData: false,
  });
}

export async function deleteTargetMetric(ref: string): Promise<void> {
  return apiNoContent(`${BASE}/metrics/${encodeURIComponent(ref)}`, { method: "DELETE" });
}

/** Redo the spreads that fell back to linear for lack of history. */
export async function recalculateTargetMetric(
  ref: string,
): Promise<{ recalculated: number; still_linear: number }> {
  return apiJson(`${BASE}/metrics/${encodeURIComponent(ref)}/recalculate`, {
    method: "POST",
    unwrapData: false,
  });
}

// ── Calls: lines ───────────────────────────────────────────────────────────

export async function listTargetLines(
  filters: TargetLineFilters = {},
): Promise<TargetLineListResponse> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return apiJson<TargetLineListResponse>(`${BASE}${suffix}`, { unwrapData: false });
}

export async function updateTargetLine(
  linePublicId: string,
  body: {
    value?: number | string;
    method?: AllocationMethod;
    weights?: Array<number | string>;
    comparator?: TargetComparator;
  },
): Promise<TargetLine> {
  return apiJson<TargetLine>(`${BASE}/${encodeURIComponent(linePublicId)}`, {
    method: "PUT",
    body: JSON.stringify(body),
    unwrapData: false,
  });
}

export async function recalculateTargetLine(
  linePublicId: string,
): Promise<TargetLine> {
  return apiJson<TargetLine>(
    `${BASE}/${encodeURIComponent(linePublicId)}/recalculate`,
    { method: "POST", unwrapData: false },
  );
}

export async function deleteTargetLine(linePublicId: string): Promise<void> {
  return apiNoContent(`${BASE}/${encodeURIComponent(linePublicId)}`, {
    method: "DELETE",
  });
}

// ── Calls: import ──────────────────────────────────────────────────────────

/**
 * Upload a CSV or XLSX and get the rows back — nothing is stored.
 *
 * `apiFetch` + `FormData` rather than `apiJson`, which sets a JSON
 * content type the moment a body is present and so destroys the multipart
 * boundary (the `BigQueryAuthorizationCreateModal` pattern).
 */
export async function parseTargetsFile(file: File): Promise<ImportParseResponse> {
  const form = new FormData();
  form.append("file", file, file.name);

  const res = await apiFetch(`${BASE}/import/parse`, {
    method: "POST",
    body: form,
  });

  const body = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (!res.ok) {
    const detail = (body as { detail?: unknown }).detail;
    const errorField = (body as { error?: unknown }).error;
    const message =
      (typeof detail === "string" ? detail : undefined) ??
      (detail as { message?: string } | undefined)?.message ??
      (typeof errorField === "string" ? errorField : undefined) ??
      (errorField as { message?: string } | undefined)?.message ??
      "We could not read that file.";
    throw new Error(String(message));
  }
  return body as ImportParseResponse;
}

export async function parseTargetsSheet(body: {
  authorization_public_id: string;
  file_id: string;
}): Promise<ImportParseResponse> {
  return apiJson<ImportParseResponse>(`${BASE}/import/parse-sheet`, {
    method: "POST",
    body: JSON.stringify(body),
    unwrapData: false,
  });
}

/** A block pasted into the grid → rows, plus what its labels mean. */
export async function parseTargetsText(body: {
  text: string;
  layout?: ImportLayout;
  default_year?: number;
}): Promise<ImportParseResponse> {
  return apiJson<ImportParseResponse>(`${BASE}/import/parse-text`, {
    method: "POST",
    body: JSON.stringify(body),
    unwrapData: false,
  });
}

export async function validateTargetsImport(body: {
  version_id?: string | null;
  rows?: ImportRow[];
  text?: string;
  default_method: AllocationMethod;
  /** Lines of this version the same apply removes (cleared cells, grain/kind changes). */
  remove_line_ids?: string[];
}): Promise<ImportValidateResponse> {
  return apiJson<ImportValidateResponse>(`${BASE}/import/validate`, {
    method: "POST",
    body: JSON.stringify(body),
    unwrapData: false,
  });
}

export async function applyTargetsImport(body: {
  version_id?: string | null;
  rows: ImportRow[];
  default_method: AllocationMethod;
  source: ImportSourceKind;
  /** Deleted in the same transaction; a vanished line makes the whole apply a 409. */
  remove_line_ids?: string[];
}): Promise<ImportApplyResponse> {
  return apiJson<ImportApplyResponse>(`${BASE}/import/apply`, {
    method: "POST",
    body: JSON.stringify(body),
    unwrapData: false,
  });
}
